/**
 * ComicViewer — displays a comic image with tap-to-fullscreen.
 * Fullscreen overlay supports pinch-to-zoom and pan on mobile.
 *
 * Usage: <ComicViewer src="/comics/abc.jpg" alt="..." />
 */

import { useState, useRef, useCallback } from 'react';
import './ComicViewer.css';

export default function ComicViewer({ src, alt, className = '' }) {
  const [fullscreen, setFullscreen] = useState(false);
  const [scale, setScale] = useState(1);
  const [translate, setTranslate] = useState({ x: 0, y: 0 });
  const touchRef = useRef({
    startDist: 0, startScale: 1, startMid: null,
    startTranslate: { x: 0, y: 0 }, panning: false,
  });

  const openFullscreen = useCallback(() => {
    setScale(1);
    setTranslate({ x: 0, y: 0 });
    setFullscreen(true);
    document.body.style.overflow = 'hidden';
  }, []);

  const closeFullscreen = useCallback(() => {
    setFullscreen(false);
    document.body.style.overflow = '';
  }, []);

  const dist = (t1, t2) => Math.hypot(t2.clientX - t1.clientX, t2.clientY - t1.clientY);
  const mid = (t1, t2) => ({
    x: (t1.clientX + t2.clientX) / 2,
    y: (t1.clientY + t2.clientY) / 2,
  });

  const handleTouchStart = useCallback((e) => {
    if (e.touches.length === 2) {
      e.preventDefault();
      const t = touchRef.current;
      t.startDist = dist(e.touches[0], e.touches[1]);
      t.startScale = scale;
      t.startMid = mid(e.touches[0], e.touches[1]);
      t.startTranslate = { ...translate };
      t.panning = false;
    } else if (e.touches.length === 1 && scale > 1.05) {
      const t = touchRef.current;
      t.startMid = { x: e.touches[0].clientX, y: e.touches[0].clientY };
      t.startTranslate = { ...translate };
      t.panning = true;
    }
  }, [scale, translate]);

  const handleTouchMove = useCallback((e) => {
    if (e.touches.length === 2) {
      e.preventDefault();
      const t = touchRef.current;
      const newDist = dist(e.touches[0], e.touches[1]);
      const newScale = Math.min(5, Math.max(1, t.startScale * (newDist / t.startDist)));
      setScale(newScale);
      const newMid = mid(e.touches[0], e.touches[1]);
      setTranslate({
        x: t.startTranslate.x + (newMid.x - t.startMid.x),
        y: t.startTranslate.y + (newMid.y - t.startMid.y),
      });
    } else if (e.touches.length === 1 && touchRef.current.panning) {
      const t = touchRef.current;
      setTranslate({
        x: t.startTranslate.x + (e.touches[0].clientX - t.startMid.x),
        y: t.startTranslate.y + (e.touches[0].clientY - t.startMid.y),
      });
    }
  }, []);

  const handleTouchEnd = useCallback(() => {
    touchRef.current.panning = false;
    if (scale <= 1.05) {
      setScale(1);
      setTranslate({ x: 0, y: 0 });
    }
  }, [scale]);

  return (
    <>
      <div
        className={`comic-viewer ${className}`}
        onClick={openFullscreen}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === 'Enter' && openFullscreen()}
      >
        <img src={src} alt={alt} className="comic-viewer__img" />
        <div className="comic-viewer__hint">tap to expand</div>
      </div>

      {fullscreen && (
        <div
          className="comic-fs"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          onClick={() => { if (scale <= 1.05) closeFullscreen(); }}
        >
          <button
            className="comic-fs__close"
            onClick={(e) => { e.stopPropagation(); closeFullscreen(); }}
            aria-label="Close"
          >
            ✕
          </button>

          <div className="comic-fs__hint">pinch to zoom · tap to close</div>

          <img
            src={src}
            alt={alt}
            draggable={false}
            onClick={(e) => e.stopPropagation()}
            className="comic-fs__img"
            style={{
              transform: `scale(${scale}) translate(${translate.x / scale}px, ${translate.y / scale}px)`,
              transition: scale <= 1.05 ? 'transform 0.2s' : 'none',
            }}
          />
        </div>
      )}
    </>
  );
}
